import { Controller } from "@hotwired/stimulus";
import { loadScript } from "../utils/script_loader";
import api from "@/utils/api";

export default class extends Controller {
  static values = {
    clientKey: String,
    customerKey: String,
    orderExtId: String,
    amount: Number,
    orderName: String,
    customerEmail: String,
    customerName: String,
    customerPhone: String,
    successUrl: String,
    identityVerified: { type: Boolean, default: false }
  };

  static targets = [
    "paymentButton"
  ];

  connect() {
    this.loadTossPaymentsSDK();
  }

  async loadTossPaymentsSDK() {
    try {
      await loadScript('https://js.tosspayments.com/v2/standard');
      this.initializePaymentWidget();
    } catch (error) {
      console.error('토스페이먼츠 SDK 로드 실패:', error);
      this.showError('결제 시스템을 불러오는데 실패했습니다.');
    }
  }

  async initializePaymentWidget() {
    try {
      if (!window.TossPayments) {
        throw new Error('TossPayments SDK가 로드되지 않았습니다.');
      }

      this.tossPayments = window.TossPayments(this.clientKeyValue);
      this.widgets = this.tossPayments.widgets({
        customerKey: this.customerKeyValue
      });

      // Set payment amount
      await this.widgets.setAmount({
        currency: 'KRW',
        value: this.amountValue
      });

      // Render payment methods
      this.paymentMethodWidget = await this.widgets.renderPaymentMethods({
        selector: '#payment-method',
        variantKey: 'DEFAULT'
      });

      // Render agreement
      this.agreementWidget = await this.widgets.renderAgreement({
        selector: '#agreement',
        variantKey: 'AGREEMENT'
      });

      if (this.identityVerifiedValue) {
        this.setupEventListeners();
      }
    } catch (error) {
      console.error('결제위젯 초기화 실패:', error);
      this.showError('결제 시스템 초기화에 실패했습니다.');
    }
  }

  setupEventListeners() {
    // 약관 동의 상태 변경 이벤트
    this.agreementWidget.on('agreementStatusChange', (agreementStatus) => {
      const agreed = agreementStatus.agreedRequiredTerms;
      this.paymentButtonTarget.disabled = !agreed;

      if (agreed) {
        this.paymentButtonTarget.innerText = "결제하기";
      } else {
        this.paymentButtonTarget.innerText = "이용약관을 먼저 동의 해주세요";
      }

      this.paymentButtonTarget.classList.toggle("noon-yellow-button", agreed)
      this.paymentButtonTarget.classList.toggle("noon-disabled-button", !agreed)
    });

    // 기본 동의이므로, 본인인증 활성화 처리 해주기
    this.paymentButtonTarget.classList.toggle("noon-yellow-button", true)
    this.paymentButtonTarget.classList.toggle("noon-disabled-button", false)
  }

  async requestPayment() {
    const tosspaymentsOrderId = crypto.randomUUID();
    if (this.paymentButtonTarget.disabled) {
      this.showError('약관에 동의해주세요.');
      return;
    }

    try {
      this.setButtonLoading(true);

      // Promise 방식으로 결제 요청
      const result = await this.widgets.requestPayment({
        orderId: tosspaymentsOrderId,
        orderName: this.orderNameValue,
        customerEmail: this.customerEmailValue,
        customerName: this.customerNameValue,
        customerMobilePhone: this.customerPhoneValue
      });

      const { paymentKey, orderId, amount } = result;
      const { value, currency } = amount; //

      // Promise 방식에서는 성공 시 결과가 직접 반환됨
      if (tosspaymentsOrderId === orderId) {
        await this.confirmPayment(tosspaymentsOrderId, paymentKey, value);
      } else {
        this.showError('결제 중 오류가 발생했습니다.');
      }

    } catch (error) {
      console.error('결제 요청 실패:', error);
      this.setButtonLoading(false);
      
      if (error.code) {
        this.showError(`결제 실패: ${error.message}`);
      } else {
        this.showError('결제 중 오류가 발생했습니다.');
      }
    }
  }

  async confirmPayment(tosspaymentsOrderId, paymentKey, amount) {
    try {
      const response = await api.post(`/market/orders/${this.orderExtIdValue}/confirm`, {
        tosspayments_order_id: tosspaymentsOrderId,
        payment_key: paymentKey,
        amount: amount
      })

      const data = response.data;

      if (data.success) {
        window.location.href = data.redirect_url;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('결제 승인 실패:', error);
      this.setButtonLoading(false);
      this.showError(error.message || '결제 승인에 실패했습니다.');
    }
  }

  setButtonLoading(loading) {
    this.paymentButtonTarget.disabled = loading;

    if (loading) {
      this.paymentButtonTarget.textContent = '결제 진행 중...';
      this.paymentButtonTarget.classList.add('opacity-75', 'cursor-not-allowed');
    } else {
      this.paymentButtonTarget.textContent = '결제하기';
      this.paymentButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed');
    }
  }

  get flashesController() {
    return this.application.getControllerForElementAndIdentifier(document.getElementById("flashesContainer"), "components--flashes")
  }

  showError(message) {
    let flashParams = { ttl: 15, stack: false, key: "error" };
    flashParams.message = message;
    this.flashesController.createFlash(flashParams);
  }

  disconnect() {
    // 컨트롤러가 해제될 때 정리 작업
    if (this.paymentMethodWidget) {
      try {
        this.paymentMethodWidget.destroy();
      } catch (error) {
        console.warn('결제위젯 정리 중 오류:', error);
      }
    }

    if (this.agreementWidget) {
      try {
        this.agreementWidget.destroy();
      } catch (error) {
        console.warn('약관위젯 정리 중 오류:', error);
      }
    }
  }
}